import { Grid, Typography, useTheme } from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	getRoute,
	type RouteObject,
} from "web_myplann/Infrastructure/Utils/UrlUtils";
import { HeaderBase } from "web_myplann/Shared/Header/HeaderBase";
import { HeaderIconButton } from "web_myplann/Shared/Header/HeaderIconButton";
import { useNavigationHeaderTitleTypographyProps } from "web_myplann/Shared/Header/useNavigationHeaderTitleTypographyProps";

type Props<TRoute extends any[] = any[]> = {
	defaultRoute?: RouteObject<TRoute> | string;
	title: string | React.ReactNode;
	endComponent?: React.ReactNode;
	startComponent?: React.ReactNode;
	disablePadding?: boolean;
	bottomComponent?: React.ReactNode;
	disableBottomPadding?: boolean;
	alwaysUseDefaultRoute?: boolean;
	bgColor?: string;
	iconColor?: string;
};

const NavigationHeader = <TRoute extends any[] = any[]>({
	defaultRoute,
	title,
	endComponent,
	disablePadding,
	startComponent,
	bottomComponent,
	disableBottomPadding,
	alwaysUseDefaultRoute = false,
	bgColor,
	iconColor,
}: Props<TRoute>) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const headerProps = useNavigationHeaderTitleTypographyProps();

	const navigateBack = () => {
		if (!defaultRoute) return;

		if (!alwaysUseDefaultRoute && location.key !== "default") {
			navigate(-1);
			return;
		}

		const to =
			typeof defaultRoute === "string" ? defaultRoute : getRoute(defaultRoute);

		navigate(to);
	};

	return (
		<HeaderBase
			disablePadding={disablePadding}
			bottomComponent={bottomComponent}
			disableBottomPadding={disableBottomPadding}
			bgColor={bgColor}
		>
			<Grid
				size={{ xs: 2 }}
				flexDirection="row"
				gap={1.2}
				display="flex"
				alignItems="center"
				component="div"
			>
				{defaultRoute && (
					<HeaderIconButton onClick={navigateBack}>
						<ArrowLeft color={iconColor ?? theme.palette.primary.main} />
					</HeaderIconButton>
				)}
				{startComponent}
			</Grid>

			<Grid
				size={{ xs: 8 }}
				display="flex"
				alignItems="center"
				justifyContent="center"
				textAlign="center"
				component="div"
			>
				{typeof title === "string" ? (
					<Typography {...headerProps}>{title}</Typography>
				) : (
					title
				)}
			</Grid>

			<Grid
				size={{ xs: 2 }}
				display="flex"
				justifyContent="flex-end"
				alignItems="center"
				gap={1.2}
				component="div"
			>
				{endComponent}
			</Grid>
		</HeaderBase>
	);
};

export default NavigationHeader;
